// === DÉTECTION D'APPAREIL ET UTILITAIRES MOBILE ===
// Détecte le type d'appareil (mobile, tablette, desktop) et fournit
// des fonctions d'optimisation conditionnelles.
// NE se base PAS uniquement sur la taille d'écran (un navigateur Desktop
// redimensionné ne doit pas déclencher le mode mobile).
//
// Si l'utilisateur a défini un override dans `DisplayModeSettings`, celui-ci
// court-circuite l'auto-détection (utile pour tester ou forcer un layout
// précis sur un device tactile).

import { DisplayModeSettings } from './DisplayModeSettings';

export type DeviceType = 'desktop' | 'tablet' | 'mobile';

// Breakpoints (en px, largeur logique)
const BREAKPOINT_MOBILE = 768;
const BREAKPOINT_TABLET = 1024;

/** Cache interne pour éviter de recalculer à chaque appel */
let _cachedDeviceType: DeviceType | null = null;
let _cachedIsTouchDevice: boolean | null = null;

/**
 * Détecte si l'appareil supporte le tactile.
 * Vérifie navigator.maxTouchPoints ET 'ontouchstart' dans window.
 */
export function isTouchDevice(): boolean {
  if (_cachedIsTouchDevice !== null) return _cachedIsTouchDevice;
  _cachedIsTouchDevice =
    navigator.maxTouchPoints > 0 ||
    'ontouchstart' in window;
  return _cachedIsTouchDevice;
}

/**
 * Détecte si l'appareil est un mobile.
 * Combine user-agent + tactile + taille d'écran.
 */
export function isMobile(): boolean {
  return getDeviceType() === 'mobile';
}

/**
 * Détecte si l'appareil est une tablette.
 */
export function isTablet(): boolean {
  return getDeviceType() === 'tablet';
}

/**
 * Retourne le type d'appareil détecté.
 * Logique : on vérifie d'abord le user-agent, puis on combine
 * avec les touch points et la taille d'écran.
 */
export function getDeviceType(): DeviceType {
  if (_cachedDeviceType !== null) return _cachedDeviceType;

  // Override utilisateur prioritaire — court-circuite l'auto-détection
  const forced = DisplayModeSettings.getForcedDeviceType();
  if (forced) {
    _cachedDeviceType = forced;
    return forced;
  }

  const ua = navigator.userAgent;
  const hasTouchPoints = navigator.maxTouchPoints > 0;

  // User-agent mobile (smartphones)
  const mobileUA = /Android.*Mobile|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  // User-agent tablette (iPad, tablettes Android)
  const tabletUA = /iPad|Android(?!.*Mobile)/i.test(ua);
  // iPad OS 13+ se fait passer pour un Mac — détecter via touch + plateforme
  const iPadOS = /Macintosh/i.test(ua) && hasTouchPoints && navigator.maxTouchPoints >= 2;
  // Détecter Windows desktop (ne pas confondre avec mobile même si tactile)
  const isWindows = /Windows/i.test(ua);

  if (mobileUA) {
    _cachedDeviceType = 'mobile';
  } else if (tabletUA || iPadOS) {
    _cachedDeviceType = 'tablet';
  } else if (isWindows) {
    // Windows = toujours desktop, même avec écran tactile ou petite fenêtre
    _cachedDeviceType = 'desktop';
  } else if (hasTouchPoints && window.innerWidth < BREAKPOINT_MOBILE) {
    // Appareil tactile non-Windows petit écran → mobile
    _cachedDeviceType = 'mobile';
  } else if (hasTouchPoints && window.innerWidth < BREAKPOINT_TABLET) {
    // Appareil tactile non-Windows moyen écran → tablette
    _cachedDeviceType = 'tablet';
  } else {
    _cachedDeviceType = 'desktop';
  }

  return _cachedDeviceType;
}

/**
 * Raccourci : vrai si mobile OU tablette (= pas desktop).
 * Utile pour activer les optimisations tactiles.
 */
export function isMobileOrTablet(): boolean {
  return getDeviceType() !== 'desktop';
}

/**
 * Applique les optimisations de performance sur mobile.
 * À appeler une fois après la création du jeu Phaser.
 */
export function applyMobileOptimizations(game: Phaser.Game): void {
  if (!isMobileOrTablet()) return;

  // Réduire le FPS cible si le CPU est faible
  const cores = navigator.hardwareConcurrency || 4;
  if (cores <= 4) {
    game.loop.targetFps = 30;
  }
}

/**
 * Réinitialise le cache (utile pour les tests).
 */
export function resetDeviceCache(): void {
  _cachedDeviceType = null;
  _cachedIsTouchDevice = null;
}
